<template>
  <div class="human-record-page">
    <div class="settings-card">
      <h2 class="page-title">
        <Icon icon="heroicons:video-camera" width="24" height="24" />
        视频合成记录
      </h2>

      <!-- 搜索区域 -->
      <div class="search-bar">
        <div class="search-left">
          <el-input v-model="search.keyword" placeholder="按任务名/视频任务ID搜索" clearable size="large" class="search-input"
            @keyup.enter="doSearch">
            <template #prefix>
              <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
            </template>
          </el-input>

          <el-button type="primary" size="large" @click="doSearch" class="search-btn">
            <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
            <span class="btn-text">搜索</span>
          </el-button>

          <el-button size="large" @click="fetchList" :loading="loading" plain>
            <Icon icon="heroicons:arrow-path" width="18" height="18" />
            <span class="btn-text">刷新</span>
          </el-button>

          <el-popconfirm v-if="selectedIds.length" :title="`确认删除选中的 ${selectedIds.length} 条记录吗？`"
            @confirm="doBatchDelete">
            <template #reference>
              <el-button type="danger" :disabled="!selectedIds.length" size="large">
                <Icon icon="heroicons:trash" width="18" height="18" />
                <span class="btn-text">批量删除（{{ selectedIds.length }}）</span>
              </el-button>
            </template>
          </el-popconfirm>
        </div>

        <div class="search-right">
          <el-button size="large" :disabled="!selectedIds.length || !hasSelectedVideos" :loading="zipMaking"
            @click="downloadBatchZip" plain>
            <Icon icon="heroicons:arrow-down-tray" width="18" height="18" />
            <span class="btn-text">批量打包下载</span>
          </el-button>

          <el-button size="large" :loading="refreshingBatch" @click="refreshPending" plain>
            <Icon icon="heroicons:arrow-path" width="18" height="18" />
            <span class="btn-text">批量刷新（{{ pendingCount }}）</span>
          </el-button>

          <el-button type="success" size="large" @click="openAdd" class="add-btn">
            <Icon icon="heroicons:plus" width="18" height="18" />
            <span class="btn-text">添加合成</span>
          </el-button>
        </div>
      </div>

      <!-- 表格包装器 -->
      <div class="table-wrapper">
        <el-table :data="list" stripe v-loading="loading" class="data-table" :row-key="row => row.id"
          @selection-change="handleSelectionChange" :height="tableHeight"
          :row-style="{ background: 'transparent' }" :cell-style="{ background: 'transparent' }"
          :header-cell-style="{ background: 'rgba(157, 78, 221, 0.15)', color: '#fff' }">

          <el-table-column type="selection" width="48" />
          <el-table-column prop="id" label="ID" width="70" />
          <el-table-column prop="human_task_name" label="任务名" min-width="200" show-overflow-tooltip />
          <el-table-column prop="human_clone_id" label="形象ID" min-width="180" show-overflow-tooltip />

          <el-table-column label="通道" width="80">
            <template #default="{ row }">
              <span class="type-tag" :class="getChannelClass(row.generate_channel)">
                {{ channelLabel(row.generate_channel) }}
              </span>
            </template>
          </el-table-column>

          <el-table-column label="音频" min-width="280">
            <template #default="{ row }">
              <div class="audio-cell" v-if="row.voice_url">
                <audio :src="row.voice_url" controls preload="none" class="audio-player"></audio>
                <button class="icon-btn" @click="copyText(row.voice_url)">
                  <Icon icon="heroicons:document-duplicate" width="14" height="14" />
                </button>
              </div>
              <span v-else class="empty-text">—</span>
            </template>
          </el-table-column>

          <el-table-column label="视频" min-width="240">
            <template #default="{ row }">
              <div v-if="row.human_synthetic_video_url" class="video-cell">
                <div class="video-thumb" @click="openPreview(row)">
                  <img v-if="row.human_synthetic_cover_url" :src="row.human_synthetic_cover_url" />
                  <div v-else class="thumb-placeholder">
                    <Icon icon="heroicons:play" width="24" height="24" />
                  </div>
                </div>
                <div class="video-actions">
                  <button class="action-btn small-btn" @click="openPreview(row)">
                    <Icon icon="heroicons:eye" width="14" height="14" />
                  </button>
                  <button class="action-btn small-btn" @click="copyText(row.human_synthetic_video_url)">
                    <Icon icon="heroicons:document-duplicate" width="14" height="14" />
                  </button>
                  <button class="action-btn small-btn" @click="downloadOne(row)">
                    <Icon icon="heroicons:arrow-down-tray" width="14" height="14" />
                  </button>
                </div>
              </div>
              <span v-else class="empty-text">—</span>
            </template>
          </el-table-column>

          <el-table-column label="状态" width="90">
            <template #default="{ row }">
              <span class="type-tag" :class="getStatusClass(row.status)">
                {{ getStatusText(row.status) }}
              </span>
            </template>
          </el-table-column>

          <el-table-column prop="video_task_id" label="视频任务ID" min-width="160" show-overflow-tooltip />
          <el-table-column prop="create_time" label="创建时间" width="170" />

          <el-table-column label="操作" width="220" fixed="right">
            <template #default="{ row }">
              <button v-if="row.human_synthetic_video_url" class="action-btn primary-btn" @click="downloadOne(row)">
                <Icon icon="heroicons:arrow-down-tray" width="14" height="14" />
                <span class="btn-text">下载</span>
              </button>
              <button v-if="row.status === 3 && row.video_task_id" class="action-btn warning-btn"
                :loading="rowRefreshingId === row.id" @click="refreshOne(row)">
                <Icon icon="heroicons:arrow-path" width="14" height="14" />
                <span class="btn-text">刷新</span>
              </button>
              <button class="action-btn danger-btn" @click="delOne(row.id)">
                <Icon icon="heroicons:trash" width="14" height="14" />
                <span class="btn-text">删除</span>
              </button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination v-model:current-page="pager.page" v-model:page-size="pager.pageSize" :total="pager.total"
          :page-sizes="[10, 20, 30, 50]" :layout="paginationLayout" @size-change="fetchList"
          @current-change="fetchList" background />
      </div>
    </div>

    <!-- 预览弹窗 -->
    <el-dialog v-model="preview.visible" :title="preview.row?.human_task_name || '视频预览'" :width="dialogWidth"
      :close-on-click-modal="true" destroy-on-close class="custom-dialog">
      <div v-if="preview.row" class="preview-container">
        <video :src="preview.row.human_synthetic_video_url" controls preload="metadata" class="preview-video"></video>
        <div class="preview-info">
          <div><span class="info-label">视频任务ID：</span>{{ preview.row.video_task_id }}</div>
          <div v-if="preview.row.duration_ms"><span class="info-label">时长：</span>{{ Math.ceil(preview.row.duration_ms / 1000) }}s</div>
        </div>
      </div>
      <template #footer>
        <button class="dialog-btn cancel-btn" @click="preview.visible = false">关闭</button>
      </template>
    </el-dialog>

    <!-- 新增合成弹窗 -->
    <el-dialog v-model="dialogVisible" title="形象视频合成" :width="dialogLargeWidth" :close-on-click-modal="false"
      destroy-on-close class="custom-dialog">
      <el-form ref="formRef" :model="form" :rules="rules" :label-width="formLabelWidth" class="clone-form">
        <el-form-item label="任务名" prop="human_task_name">
          <el-input v-model="form.human_task_name" placeholder="支持多个，用中/英逗号分隔" size="large">
            <template #append>
              <button class="input-append-btn" @click="insertComma('human_task_name')" type="button">
                <Icon icon="heroicons:plus" width="14" height="14" />
                插入分隔符
              </button>
            </template>
          </el-input>
          <div class="form-hint">多个任务名会按顺序与素材/形象组合</div>
        </el-form-item>

        <el-form-item label="选择形象" prop="human_clone_id">
          <el-select v-model="form.human_clone_id" multiple filterable collapse-tags collapse-tags-tooltip
            placeholder="可多选；scene_task_id 唯一" style="width: 100%" size="large">
            <el-option v-for="item in figures" :key="item.scene_task_id"
              :label="`${item.human_name}（${item.scene_task_id}）`" :value="item.scene_task_id" />
          </el-select>
        </el-form-item>

        <el-form-item label="音频来源">
          <div class="radio-group">
            <label class="radio-item" :class="{ active: form.voice_type === 1 }">
              <input type="radio" v-model="form.voice_type" :value="1" />
              <span>本地/直链</span>
            </label>
            <label class="radio-item" :class="{ active: form.voice_type === 2 }">
              <input type="radio" v-model="form.voice_type" :value="2" />
              <span>历史音频</span>
            </label>
          </div>
        </el-form-item>

        <!-- 本地/直链 -->
        <template v-if="form.voice_type === 1">
          <el-form-item label="音频直链">
            <el-input v-model="manualVoiceUrls" type="textarea" :rows="3" class="mono-input"
              placeholder="每行一个，或用英文逗号 , 分隔；上传成功的链接会自动填充到此处" />
          </el-form-item>

          <el-form-item label="上传音频">
            <div class="upload-area">
              <el-upload class="drop-card" drag multiple :auto-upload="false" :show-file-list="false"
                accept=".mp3,.wav,.m4a,.aac" :on-change="onPickLocalAudio">
                <div class="drop-inner">
                  <div class="drop-icon">
                    <Icon icon="heroicons:cloud-arrow-up" width="32" height="32" />
                  </div>
                  <div class="drop-title">将音频拖到此处</div>
                  <div class="drop-sub">或 <span class="link">点击选择</span>（可多选）</div>
                  <div class="drop-meta">建议时长 ≥ 3 秒</div>
                </div>
              </el-upload>

              <div v-if="uploadQueue.length" class="queue">
                <div class="queue-title">上传进度</div>
                <div v-for="q in uploadQueue" :key="q.id" class="queue-item">
                  <span class="q-name" :title="q.name">{{ q.name }}</span>
                  <el-progress :percentage="q.percent" :status="q.status" :stroke-width="6" />
                </div>
              </div>

              <div v-if="voiceUrlList.length" class="url-chips">
                <span v-for="(u, i) in voiceUrlList" :key="u" class="url-chip">
                  {{ truncateUrl(u) }}
                  <span v-if="durMap[u]" class="chip-duration">（{{ Math.ceil((durMap[u] || 0) / 1000) }}s）</span>
                  <button class="chip-close" @click="removeVoiceUrl(i)">
                    <Icon icon="heroicons:x-mark" width="12" height="12" />
                  </button>
                </span>
              </div>
            </div>
          </el-form-item>
        </template>

        <!-- 历史音频 -->
        <template v-else>
          <el-form-item label="历史音频" prop="history_voice_ids">
            <el-select v-model="selectedHistoryIds" multiple filterable collapse-tags collapse-tags-tooltip
              placeholder="选择历史已合成音频（支持多选）" style="width: 100%" size="large">
              <el-option v-for="r in historyAudios" :key="r.id"
                :label="`${r.voice_task_name || '未命名'}（#${r.id}）`" :value="r.id">
                <div class="opt-line">
                  <span class="opt-name">{{ r.voice_task_name || '未命名' }}</span>
                  <span class="opt-sub">#{{ r.id }}</span>
                </div>
                <div class="opt-sub opt-url">{{ truncateUrl(r.human_voice_synthetic_url) }}</div>
                <div class="opt-sub">时长：{{ durDisplay(r.human_voice_synthetic_url) }}</div>
              </el-option>
            </el-select>

            <div v-if="selectedHistoryIds.length" class="url-chips mt6">
              <span v-for="hid in selectedHistoryIds" :key="hid" class="url-chip">
                {{ findHistoryLabel(hid) }}
                <span class="chip-duration">（{{ durDisplay(historyUrlById(hid)) }}）</span>
                <button class="chip-close" @click="removeHistoryId(hid)">
                  <Icon icon="heroicons:x-mark" width="12" height="12" />
                </button>
              </span>
            </div>
            <div class="form-hint">首次选择历史音频时将自动拉取时长用于预估；实际扣点以平台返回为准。</div>
          </el-form-item>
        </template>

        <el-form-item label="生成通道">
          <el-select v-model="form.generate_channel" style="width: 200px" size="large">
            <el-option :value="1" label="普通通道" />
            <el-option :value="2" label="PRO 通道" />
            <el-option :value="3" label="极速通道" />
          </el-select>
          <div class="form-hint mt6">
            单价（每秒）：普通 {{ price.normal }}/s；PRO {{ price.pro }}/s；极速 {{ price.turbo }}/s
          </div>
          <div class="price-tip">
            <Icon icon="heroicons:information-circle" width="14" height="14" />
            {{ priceTip }}
          </div>
        </el-form-item>

        <el-form-item label="策略">
          <label class="checkbox-label">
            <input type="checkbox" v-model="form.apply_to_all" />
            <span>全员合成：每段音频对所有已选形象各合成一次</span>
          </label>
        </el-form-item>

        <el-form-item>
          <label class="checkbox-label">
            <input type="checkbox" v-model="agree" :disabled="!agreeEnabled" />
            <span>我已阅读并同意</span>
            <button class="link-btn" type="button" @click.prevent="openAgreement">
              《克隆使用协议》
            </button>
          </label>
          <span v-if="agreeCountdown > 0" class="countdown-text">（请至少阅读 {{ agreeCountdown }} 秒）</span>
        </el-form-item>
      </el-form>

      <template #footer>
        <button class="dialog-btn cancel-btn" @click="dialogVisible = false">取消</button>
        <button class="dialog-btn confirm-btn" :disabled="!agreeReady || !canSubmit || submitting" @click="submit">
          <Icon icon="heroicons:sparkles" width="16" height="16" />
          开始合成（预计 {{ totalCost }} 积分 / {{ totalSeconds }} 秒）
        </button>
      </template>
    </el-dialog>

    <!-- 协议弹窗 -->
    <el-dialog v-model="agreementDialogVisible" :title="agreementTitle || '克隆使用协议'" :width="dialogWidth"
      :close-on-click-modal="false" @closed="stopAgreeCountdown" class="custom-dialog">
      <div v-loading="agreementLoading" class="agreement-content">
        <div class="agreement-meta" v-if="agreementUpdatedAt">
          最近更新：{{ agreementUpdatedAt }}
        </div>
        <div class="agreement-text" v-if="agreementContent" v-html="agreementContent"></div>
        <el-empty v-else description="未读取到协议内容" />
      </div>
      <template #footer>
        <button class="dialog-btn cancel-btn" @click="agreementDialogVisible = false">关闭</button>
        <button class="dialog-btn confirm-btn" :disabled="agreeCountdown > 0" @click="acceptAgreement">
          {{ agreeCountdown > 0 ? `请阅读（${agreeCountdown}s）` : '我已阅读并同意' }}
        </button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import Cookies from 'js-cookie'
import config from '@/config.js'
import { Icon } from '@iconify/vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { saveAs } from 'file-saver'
import { getUploadSignature, uploadToOSS, beforeUpload, getVideoDuration } from '@/utils/uploadUtils'

export default {
  name: 'HumanRecord',
  components: { Icon },
  props: {
    cloneId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      list: [],
      pager: { page: 1, pageSize: 10, total: 0 },
      search: { keyword: '' },
      selectedIds: [],
      figures: [],
      historyAudios: [],
      dialogVisible: false,
      submitting: false,
      refreshingBatch: false,
      rowRefreshingId: null,
      zipMaking: false,
      preview: { visible: false, row: null },
      agree: false,
      agreeEnabled: false,
      agreeCountdown: 0,
      agreeTimer: null,
      agreementDialogVisible: false,
      agreementLoading: false,
      agreementTitle: '',
      agreementUpdatedAt: '',
      agreementContent: '',
      form: {
        human_task_name: '',
        human_clone_id: [],
        voice_type: 1,
        generate_channel: 1,
        apply_to_all: false
      },
      rules: {
        human_task_name: [{ required: true, message: '请输入任务名', trigger: 'blur' }],
        human_clone_id: [{ required: true, message: '请选择形象', trigger: 'change' }]
      },
      manualVoiceUrls: '',
      ossVoiceUrls: [],
      uploadQueue: [],
      processedUids: new Set(),
      durMap: {},
      selectedHistoryIds: [],
      price: { normal: 0, pro: 0, turbo: 0 },
      screenWidth: window.innerWidth,
      autoTimer: null
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}')
    },
    pendingCount() {
      return (this.list || []).filter(x => Number(x.status) === 3 && x.video_task_id).length
    },
    hasSelectedVideos() {
      return this.list.some(r => this.selectedIds.includes(r.id) && r.human_synthetic_video_url)
    },
    voiceUrlList() {
      const manual = (this.manualVoiceUrls || '')
        .split(/[,\n]/g)
        .map(s => s.trim())
        .filter(Boolean)
      return Array.from(new Set([...manual, ...this.ossVoiceUrls]))
    },
    finalVoiceSet() {
      if (this.form.voice_type === 1) {
        return this.voiceUrlList.map(u => ({ id: null, url: u, duration_ms: this.durMap[u] || null }))
      } else {
        return this.selectedHistoryIds
          .map(id => {
            const rec = this.historyAudios.find(x => x.id === id)
            if (!rec) return null
            const url = rec.human_voice_synthetic_url || ''
            return { id: rec.id, url, duration_ms: this.durMap[url] || null }
          })
          .filter(Boolean)
      }
    },
    unitForChannel() {
      return this.form.generate_channel === 2 ? this.price.pro : (this.form.generate_channel === 3 ? this.price.turbo : this.price.normal)
    },
    baseSeconds() {
      return this.finalVoiceSet.reduce((s, v) => {
        const ms = v.duration_ms
        if (!ms || ms <= 0) return s
        return s + Math.max(1, Math.ceil(ms / 1000))
      }, 0)
    },
    totalSeconds() {
      const base = this.baseSeconds
      return this.form.apply_to_all ? base * Math.max(1, this.form.human_clone_id.length) : base
    },
    totalCost() {
      return (this.totalSeconds * this.unitForChannel).toFixed(2)
    },
    priceTip() {
      const u = this.unitForChannel
      const note = this.form.apply_to_all
        ? `（全员合成：可估秒数 × 形象数 ${Math.max(1, this.form.human_clone_id.length)}）`
        : ''
      return `当前通道单价：${u} 积分/秒；可估：${this.totalSeconds}s × ${u} = ${this.totalCost} 积分 ${note}。历史音频因无法预先获取时长，实际扣点以平台返回为准。`
    },
    agreeReady() {
      return this.agree && this.agreeCountdown === 0
    },
    canSubmit() {
      const hasFigures = this.form.human_clone_id.length > 0
      const hasAudio = this.form.voice_type === 1 ? this.voiceUrlList.length > 0 : this.selectedHistoryIds.length > 0
      return hasFigures && hasAudio
    },
    tableHeight() {
      if (this.screenWidth < 768) return 'calc(100vh - 320px)'
      return 'calc(100vh - 280px)'
    },
    paginationLayout() {
      if (this.screenWidth < 576) return 'prev, pager, next'
      if (this.screenWidth < 768) return 'total, prev, pager, next'
      return 'total, sizes, prev, pager, next, jumper'
    },
    dialogWidth() {
      if (this.screenWidth < 576) return '95%'
      if (this.screenWidth < 768) return '90%'
      return '720px'
    },
    dialogLargeWidth() {
      if (this.screenWidth < 576) return '95%'
      if (this.screenWidth < 768) return '90%'
      return '880px'
    },
    formLabelWidth() {
      if (this.screenWidth < 576) return '80px'
      return '100px'
    }
  },
  watch: {
    async selectedHistoryIds(newIds) {
      const urls = newIds
        .map(id => this.historyAudios.find(x => x.id === id))
        .filter(Boolean)
        .map(r => r.human_voice_synthetic_url)
        .filter(Boolean)
      for (const u of urls) {
        if (!this.durMap[u]) {
          await this.fetchDurationForUrl(u)
        }
      }
    },
    cloneId: {
      immediate: true,
      handler(newId) {
        if (newId) {
          this.$nextTick(() => {
            setTimeout(() => {
              this.openAddWithCloneId(newId)
            }, 500)
          })
        }
      }
    }
  },
  mounted() {
    this.fetchList()
    this.fetchFigures()
    this.fetchHistoryAudios()
    this.autoTimer = setInterval(() => {
      if (this.pendingCount > 0 && !this.refreshingBatch) {
        this.refreshPending()
      }
    }, 15000)
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    if (this.autoTimer) clearInterval(this.autoTimer)
    window.removeEventListener('resize', this.handleResize)
    this.stopAgreeCountdown()
  },
  methods: {
    handleResize() {
      this.screenWidth = window.innerWidth
    },
    truncateUrl(url) {
      if (!url) return ''
      if (url.length > 50) {
        return url.substring(0, 30) + '...' + url.substring(url.length - 15)
      }
      return url
    },
    channelLabel(v) {
      return v === 2 ? 'PRO' : (v === 3 ? '极速' : '普通')
    },
    getChannelClass(v) {
      if (v === 2) return 'channel-pro'
      if (v === 3) return 'channel-turbo'
      return 'channel-normal'
    },
    getStatusClass(status) {
      if (status === 1) return 'status-success'
      if (status === 3) return 'status-processing'
      return 'status-failed'
    },
    getStatusText(status) {
      if (status === 1) return '成功'
      if (status === 3) return '处理中'
      return '失败'
    },
    openPreview(row) {
      this.preview.row = row
      this.preview.visible = true
    },
    openAddWithCloneId(cloneId) {
      this.openAdd()
      setTimeout(() => {
        if (this.figures.some(fig => fig.scene_task_id === cloneId)) {
          this.form.human_clone_id = [cloneId]
          ElMessage.success(`已自动选择形象: ${cloneId}`)
        } else {
          ElMessage.warning(`未找到ID为 ${cloneId} 的形象`)
        }
      }, 800)
    },
    async downloadBatchZip() {
      const rows = this.list
        .filter(r => this.selectedIds.includes(r.id))
        .filter(r => r.human_synthetic_video_url)
      if (!rows.length) {
        ElMessage.warning('请选择至少一个已合成的视频')
        return
      }
      this.zipMaking = true
      try {
        const items = rows.map(r => ({
          url: r.human_synthetic_video_url,
          filename: this.buildSafeName(`${r.human_task_name || 'video'}_${r.id}.mp4`)
        }))
        const res = await fetch(`${config.BASE_URL}/shop/api/zip_videos.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const blob = await res.blob()
        saveAs(blob, `videos_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '')}.zip`)
        ElMessage.success(`打包完成，共 ${rows.length} 个视频`)
      } catch (e) {
        console.error(e)
        ElMessage.error('打包失败，请重试')
      } finally {
        this.zipMaking = false
      }
    },
    buildSafeName(name) {
      return String(name)
        .replace(/[\\/:*?"<>|]/g, '_')
        .replace(/\s+/g, '_')
        .slice(0, 120)
    },
    downloadOne(row) {
      if (!row?.human_synthetic_video_url) {
        ElMessage.warning('没有可下载的视频')
        return
      }
      const a = document.createElement('a')
      a.href = row.human_synthetic_video_url
      a.download = `${row.human_task_name || 'video'}_${row.id}.mp4`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },
    async refreshPending() {
      try {
        this.refreshingBatch = true
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/human_clone_synth_task_query.php`, {
          action: 'refresh_pending',
          store_id: this.userInfo.id,
          limit: 100
        })
        if (data?.success) {
          const updated = data.updated || 0
          if (updated > 0) {
            ElMessage.success(`已刷新：${updated} 条`)
          } else {
            ElMessage.info('暂无可更新的任务')
          }
          await this.fetchList()
        } else {
          ElMessage.error(data?.error || '批量刷新失败')
        }
      } catch (e) {
        ElMessage.error('批量刷新失败')
      } finally {
        this.refreshingBatch = false
      }
    },
    async refreshOne(row) {
      if (!row?.video_task_id) return
      try {
        this.rowRefreshingId = row.id
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/human_clone_synth_task_query.php`, {
          action: 'query_one',
          store_id: this.userInfo.id,
          video_task_id: String(row.video_task_id)
        })
        if (data?.success) {
          ElMessage.success('已刷新该任务')
          await this.fetchList()
        } else {
          ElMessage.error(data?.error || '刷新失败')
        }
      } catch (e) {
        ElMessage.error('刷新失败')
      } finally {
        this.rowRefreshingId = null
      }
    },
    async fetchList() {
      try {
        this.loading = true
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/human_clone_synthetic_record.php`, {
          action: this.search.keyword ? 'search' : 'list',
          store_id: this.userInfo.id,
          keyword: this.search.keyword,
          page: this.pager.page,
          page_size: this.pager.pageSize
        })
        this.list = data.data || []
        this.pager.total = data.pagination ? data.pagination.total : (this.list?.length || 0)
        if (!this.list.length && this.pager.page > 1) {
          this.pager.page = this.pager.page - 1
          await this.fetchList()
        }
        this.selectedIds = []
      } catch (e) {
        ElMessage.error('获取列表失败')
      } finally {
        this.loading = false
      }
    },
    doSearch() {
      this.pager.page = 1
      this.fetchList()
    },
    handleSelectionChange(rows) {
      this.selectedIds = rows.map(r => r.id)
    },
    async doBatchDelete() {
      if (!this.selectedIds.length) return
      try {
        this.loading = true
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/human_clone_synthetic_record.php`, {
          action: 'batch_delete',
          store_id: this.userInfo.id,
          ids: this.selectedIds
        })
        if (data.success) {
          ElMessage.success('批量删除成功')
          await this.fetchList()
        } else {
          ElMessage.error(data.error || '批量删除失败')
        }
      } catch (e) {
        ElMessage.error('批量删除失败')
      } finally {
        this.loading = false
      }
    },
    async delOne(id) {
      try {
        await ElMessageBox.confirm('确定删除该记录吗？', '提示', { type: 'warning', confirmButtonText: '确认删除', cancelButtonText: '取消' })
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/human_clone_synthetic_record.php`, {
          action: 'delete',
          id,
          store_id: this.userInfo.id
        })
        if (data.success) {
          ElMessage.success('已删除')
          this.fetchList()
        } else {
          ElMessage.error(data.error || '删除失败')
        }
      } catch (e) {
        if (e !== 'cancel') ElMessage.error('删除失败')
      }
    },
    async fetchFigures() {
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/human_clone.php`, {
          action: 'list_all',
          store_id: this.userInfo.id
        })
        const rows = data?.data || []
        this.figures = rows
          .filter(r => r.scene_task_id)
          .map(r => ({ scene_task_id: r.scene_task_id, human_name: r.human_name || r.scene_task_id }))
      } catch (e) {
        this.figures = []
      }
    },
    async fetchHistoryAudios() {
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/voice_clone_synthetic_record.php`, {
          action: 'list_all',
          store_id: this.userInfo.id
        })
        this.historyAudios = data?.data || []
        const firstBatch = this.historyAudios.slice(0, 20)
        for (const r of firstBatch) {
          const url = r?.human_voice_synthetic_url
          if (url && !this.durMap[url]) {
            await this.fetchDurationForUrl(url)
          }
        }
      } catch (e) {
        this.historyAudios = []
      }
    },
    async openAdd() {
      this.dialogVisible = true
      this.agree = false
      this.agreeEnabled = false
      this.stopAgreeCountdown()
      this.form = { human_task_name: '', human_clone_id: [], voice_type: 1, generate_channel: 1, apply_to_all: false }
      this.manualVoiceUrls = ''
      this.ossVoiceUrls = []
      this.uploadQueue = []
      this.processedUids.clear()
      this.durMap = {}
      this.selectedHistoryIds = []
      this.$nextTick(() => this.$refs.formRef?.clearValidate?.())
      try {
        const { data } = await axios.get(`${config.BASE_URL}/shop/api/get_digital_human_config.php`)
        const n = Number(parseFloat(data?.data?.digital_human_normal_synth_num || 0).toFixed(2))
        const p = Number(parseFloat(data?.data?.digital_human_pro_synth_num || 0).toFixed(2))
        const t = Number(parseFloat(data?.data?.digital_human_turbo_synth_num || 0).toFixed(2))
        this.price = { normal: isNaN(n) ? 0 : n, pro: isNaN(p) ? 0 : p, turbo: isNaN(t) ? 0 : t }
      } catch (e) {
        this.price = { normal: 0, pro: 0, turbo: 0 }
      }
    },
    async openAgreement() {
      try {
        this.agreementDialogVisible = true
        this.agreementLoading = true
        const { data } = await axios.get(`${config.BASE_URL}/shop/api/get_clone_agreement.php`)
        if (data?.code === 200 && data?.data) {
          this.agreementTitle = data.data.title || '克隆使用协议'
          this.agreementUpdatedAt = data.data.updated_at || ''
          this.agreementContent = data.data.content || ''
        } else {
          this.agreementTitle = '克隆使用协议'
          this.agreementUpdatedAt = ''
          this.agreementContent = ''
        }
      } catch (e) {
        ElMessage.error('读取使用协议失败')
        this.agreementContent = ''
      } finally {
        this.agreementLoading = false
        this.startAgreeCountdown(3)
      }
    },
    startAgreeCountdown(sec) {
      this.stopAgreeCountdown()
      this.agreeCountdown = sec
      this.agreeTimer = setInterval(() => {
        if (this.agreeCountdown > 0) this.agreeCountdown--
        if (this.agreeCountdown <= 0) this.stopAgreeCountdown()
      }, 1000)
    },
    stopAgreeCountdown() {
      if (this.agreeTimer) clearInterval(this.agreeTimer)
      this.agreeTimer = null
    },
    acceptAgreement() {
      if (this.agreeCountdown > 0) return
      this.agreementDialogVisible = false
      this.agreeEnabled = true
      this.agree = true
    },
    insertComma(field) {
      const v = (this.form[field] || '')
      const ends = /[，,]\s*$/.test(v)
      this.form[field] = ends ? v + ' ' : (v ? v + ', ' : ', ')
    },
    copyText(text) {
      if (!text) return
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(() => ElMessage.success('已复制'))
      } else {
        const ta = document.createElement('textarea')
        ta.value = text
        ta.style.position = 'fixed'
        ta.style.left = '-9999px'
        document.body.appendChild(ta)
        ta.select()
        try {
          document.execCommand('copy')
          ElMessage.success('已复制')
        } catch (e) {
          ElMessage.error('复制失败')
        } finally {
          document.body.removeChild(ta)
        }
      }
    },
    findHistoryLabel(id) {
      const r = this.historyAudios.find(x => x.id === id)
      return r ? `${r.voice_task_name || '未命名'}（#${r.id}）` : `#${id}`
    },
    durDisplay(url) {
      if (!url) return '—'
      const ms = this.durMap[url]
      return ms && ms > 0 ? `${Math.ceil(ms / 1000)}s` : '—'
    },
    historyUrlById(id) {
      const r = this.historyAudios.find(x => x.id === id)
      return r?.human_voice_synthetic_url || ''
    },
    async fetchDurationForUrl(url) {
      if (!url) return
      try {
        const api = `https://szr-api.aijzdi.com/auth/wyjuzhen/audio_duration.php?audio_url=${encodeURIComponent(url)}`
        const { data } = await axios.get(api)
        if (data?.success && typeof data.duration_seconds === 'number') {
          const secs = Math.ceil(data.duration_seconds)
          const ms = secs * 1000
          this.durMap = { ...this.durMap, [url]: ms }
        }
      } catch (e) {
        // 静默失败
      }
    },
    async onPickLocalAudio(uploadFile) {
      try {
        const uid = uploadFile?.uid
        const file = uploadFile?.raw || uploadFile
        if (!file) return
        if (uid && this.processedUids.has(uid)) return
        if (uid) this.processedUids.add(uid)

        const q = {
          id: uid || `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
          name: file.name || '未命名',
          percent: 0,
          status: undefined,
          ms: null
        }
        this.uploadQueue.push(q)

        const ok = await beforeUpload(file, { type: 'audio' })
        if (!ok) {
          q.status = 'exception'
          return
        }
        q.percent = 10

        try {
          const secs = await getVideoDuration(file)
          const ms = isNaN(secs) ? null : Math.round(secs * 1000)
          if (ms != null && ms < 1000) ElMessage.warning(`${q.name} 时长较短（≈${Math.ceil(ms / 1000)}s）`)
          if (ms != null) q.ms = ms
        } catch (e) {
          // 静默
        }

        q.percent = 45
        const sign = await getUploadSignature('audio')
        const res = await uploadToOSS(file, sign)
        q.percent = 90

        if (res?.success && res.url) {
          this.ossVoiceUrls.push(res.url)
          if (q.ms != null) this.durMap = { ...this.durMap, [res.url]: q.ms }
          q.percent = 100
          q.status = 'success'
          ElMessage.success(`上传成功：${q.name}`)
          const manualParts = (this.manualVoiceUrls || '').split(/[,\n]/g).map(s => s.trim()).filter(Boolean)
          const all = Array.from(new Set([...manualParts, ...this.ossVoiceUrls]))
          this.manualVoiceUrls = all.join('\n')
        } else {
          q.status = 'exception'
          ElMessage.error(`上传失败：${q.name}`)
        }
      } catch (err) {
        ElMessage.error(err?.message || '上传失败')
      }
    },
    removeVoiceUrl(i) {
      const url = this.voiceUrlList[i]
      this.ossVoiceUrls = this.ossVoiceUrls.filter(u => u !== url)
      this.manualVoiceUrls = this.voiceUrlList.filter(u => u !== url).join('\n')
      const next = { ...this.durMap }
      delete next[url]
      this.durMap = next
    },
    removeHistoryId(id) {
      this.selectedHistoryIds = this.selectedHistoryIds.filter(x => x !== id)
    },
    async submit() {
      this.$refs.formRef.validate(async (ok) => {
        if (!ok) return
        if (!this.agreeReady) {
          ElMessage.warning('请先阅读并同意克隆使用协议')
          return
        }
        if (!this.canSubmit) {
          ElMessage.warning('请选择形象和音频')
          return
        }
        this.submitting = true
        try {
          const nameList = (this.form.human_task_name || '').split(/[，,]/g).map(s => s.trim()).filter(Boolean)
          const taskNamePayload = nameList.length > 1 ? nameList : (nameList[0] || '')

          const voice_type = this.form.voice_type
          let voice_url = []
          let history_voice_id = []
          let duration_ms_list = []

          if (voice_type === 1) {
            voice_url = this.finalVoiceSet.map(x => x.url)
            duration_ms_list = this.finalVoiceSet.map(x => x.duration_ms || null)
          } else {
            history_voice_id = this.finalVoiceSet.map(x => x.id)
            voice_url = this.finalVoiceSet.map(x => x.url)
            duration_ms_list = this.finalVoiceSet.map(x => x.duration_ms || null)
          }

          const payload = {
            action: 'add',
            store_id: this.userInfo.id,
            human_task_name: taskNamePayload,
            human_clone_id: this.form.human_clone_id.length > 1 ? this.form.human_clone_id : (this.form.human_clone_id[0] || ''),
            voice_type,
            voice_url: voice_url.length > 1 ? voice_url : (voice_url[0] || ''),
            history_voice_id: history_voice_id.length > 1 ? history_voice_id : (history_voice_id[0] || ''),
            duration_ms: duration_ms_list.length > 1 ? duration_ms_list : (duration_ms_list[0] || null),
            generate_channel: this.form.generate_channel,
            apply_to_all: this.form.apply_to_all,
            callback_url: 'https://www.baidu.com'
          }

          const { data } = await axios.post(`${config.BASE_URL}/shop/api/human_clone_synthetic_record.php`, payload)
          if (data.success) {
            ElMessage.success('已提交视频合成任务')
            this.dialogVisible = false
            this.fetchList()
          } else {
            ElMessage.error(data.error || '提交失败')
          }
        } catch (e) {
          ElMessage.error('提交失败')
        } finally {
          this.submitting = false
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
.human-record-page {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.settings-card {
  background: rgba(18, 14, 33, 0.6);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(157, 78, 221, 0.2);
  padding: 24px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 576px) {
    padding: 12px;
  }
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: #c084fc;
  }

  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 16px;
  }
}

.search-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;

  .search-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    flex: 1;
  }

  .search-right {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .search-input {
    flex: 1;
    min-width: 200px;
  }

  @media (max-width: 768px) {
    flex-direction: column;

    .search-left,
    .search-right {
      width: 100%;
    }

    .search-left .el-input {
      width: 100% !important;
    }

    .add-btn {
      width: 100%;
    }
  }
}

.table-wrapper {
  position: relative;
  width: 100%;
  overflow-x: auto;
  margin-top: 20px;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(157, 78, 221, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(157, 78, 221, 0.4);
    border-radius: 4px;

    &:hover {
      background: rgba(157, 78, 221, 0.6);
    }
  }
}

:deep(.data-table) {
  --el-table-bg-color: transparent !important;
  --el-table-tr-bg-color: transparent !important;
  --el-table-header-bg-color: transparent !important;
  --el-table-row-hover-bg-color: rgba(157, 78, 221, 0.1) !important;
  --el-table-current-row-bg-color: rgba(157, 78, 221, 0.15) !important;
  --el-table-stripe-row-bg-color: rgba(157, 78, 221, 0.05) !important;
  --el-table-border-color: rgba(157, 78, 221, 0.2) !important;

  background-color: transparent !important;
  color: #e0e0e0;

  &::before {
    display: none;
  }

  .el-table__header-wrapper {
    background-color: transparent !important;

    tr {
      background-color: transparent !important;

      th:not(.el-table-fixed-column--right) {
        background: rgba(157, 78, 221, 0.2) !important;
        color: #fff !important;
        font-weight: 600;
        border-bottom: 1px solid rgba(157, 78, 221, 0.3) !important;

        .cell {
          color: #fff !important;
        }
      }
    }
  }

  .el-table__body-wrapper {
    background-color: transparent !important;

    tr {
      background-color: transparent !important;

      td:not(.el-table-fixed-column--right) {
        background-color: transparent !important;
        border-bottom: 1px solid rgba(157, 78, 221, 0.1);
        color: #e0e0e0;
      }

      &.el-table__row--striped td:not(.el-table-fixed-column--right) {
        background-color: rgba(157, 78, 221, 0.05) !important;
      }

      &:hover td:not(.el-table-fixed-column--right) {
        background-color: rgba(157, 78, 221, 0.1) !important;
      }
    }
  }

  .el-table-fixed-column--right {
    background-color: #120e21 !important;
    backdrop-filter: none !important;
  }

  th.el-table-fixed-column--right {
    background-color: #2a1f45 !important;
  }

  .el-table__row--striped .el-table-fixed-column--right {
    background-color: #1a1430 !important;
  }

  .el-table__empty-block {
    background-color: transparent !important;

    .el-table__empty-text {
      color: #a0a0c0 !important;
    }
  }
}

.type-tag {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;

  &.channel-normal {
    background: rgba(157, 78, 221, 0.15);
    color: #c084fc;
  }

  &.channel-pro {
    background: rgba(245, 158, 11, 0.15);
    color: #fbbf24;
  }

  &.channel-turbo {
    background: rgba(239, 68, 68, 0.15);
    color: #f87171;
  }

  &.status-success {
    background: rgba(74, 222, 128, 0.15);
    color: #4ade80;
  }

  &.status-processing {
    background: rgba(157, 78, 221, 0.15);
    color: #c084fc;
  }

  &.status-failed {
    background: rgba(239, 68, 68, 0.15);
    color: #f87171;
  }
}

.empty-text {
  color: #7a7a9a;
}

.audio-cell {
  display: flex;
  align-items: center;
  gap: 8px;

  .audio-player {
    height: 36px;
    max-width: 180px;
    border-radius: 20px;
  }
}

.video-cell {
  display: flex;
  align-items: center;
  gap: 12px;

  .video-thumb {
    width: 80px;
    height: 60px;
    background: #0a0a0a;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .thumb-placeholder {
      color: #5a5a7a;
    }
  }

  .video-actions {
    display: flex;
    gap: 6px;
  }
}

.icon-btn {
  background: rgba(157, 78, 221, 0.15);
  border: none;
  border-radius: 6px;
  padding: 6px;
  cursor: pointer;
  color: #c084fc;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(157, 78, 221, 0.25);
  }
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  margin: 0 2px;

  &.primary-btn {
    background: linear-gradient(135deg, #6546b4, #9d4edd);
    color: #fff;

    &:hover {
      transform: translateY(-1px);
    }
  }

  &.warning-btn {
    background: rgba(245, 158, 11, 0.15);
    border: 1px solid rgba(245, 158, 11, 0.4);
    color: #fbbf24;

    &:hover {
      background: rgba(245, 158, 11, 0.25);
    }
  }

  &.danger-btn {
    background: rgba(239, 68, 68, 0.15);
    border: 1px solid rgba(239, 68, 68, 0.4);
    color: #f87171;

    &:hover {
      background: rgba(239, 68, 68, 0.25);
    }
  }

  &.small-btn {
    padding: 5px 8px;
  }

  @media (max-width: 576px) {
    .btn-text {
      display: none;
    }
  }
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: center;
  }
}

:deep(.el-pagination) {
  --el-pagination-bg-color: transparent;
  --el-pagination-button-bg-color: rgba(157, 78, 221, 0.1);
  --el-pagination-hover-color: #c084fc;

  .btn-prev,
  .btn-next,
  .el-pager li {
    background: rgba(157, 78, 221, 0.1);
    border: 1px solid rgba(157, 78, 221, 0.3);
    color: #a0a0c0;
    border-radius: 8px;
    min-width: 32px;
    height: 32px;

    &:hover {
      background: rgba(157, 78, 221, 0.2);
      color: #c084fc;
    }

    &.active {
      background: linear-gradient(135deg, #6546b4, #9d4edd);
      color: #fff;
      border-color: transparent;
    }
  }

  .el-pagination__total,
  .el-pagination__jump {
    color: #a0a0c0;
  }

  @media (max-width: 576px) {
    .btn-prev,
    .btn-next,
    .el-pager li {
      min-width: 28px;
      height: 28px;
      margin: 0 2px;
    }
  }
}

:deep(.custom-dialog) {
  --el-dialog-bg-color: rgba(18, 14, 33, 0.98);
  --el-dialog-border-radius: 20px;

  .el-dialog__header {
    border-bottom: 1px solid rgba(157, 78, 221, 0.2);
    padding: 20px 24px;

    .el-dialog__title {
      color: #fff;
      font-weight: 600;
    }
  }

  .el-dialog__body {
    padding: 24px;
  }

  .el-dialog__footer {
    border-top: 1px solid rgba(157, 78, 221, 0.2);
    padding: 16px 24px;
  }

  @media (max-width: 576px) {
    .el-dialog__header {
      padding: 16px;
    }
    .el-dialog__body {
      padding: 16px;
    }
    .el-dialog__footer {
      padding: 12px 16px;
    }
  }
}

.clone-form {
  :deep(.el-form-item__label) {
    color: #a0a0c0;
    font-weight: 500;
  }

  :deep(.el-input__wrapper) {
    background: rgba(26, 21, 53, 0.8);
    border: 1px solid rgba(157, 78, 221, 0.3);
    border-radius: 10px;
    box-shadow: none;

    &:hover {
      border-color: rgba(157, 78, 221, 0.6);
    }

    &.is-focus {
      border-color: #9d4edd;
      box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
    }

    .el-input__inner {
      color: #fff;
      background: transparent;
    }
  }

  :deep(.el-textarea__inner) {
    background: rgba(26, 21, 53, 0.8);
    border: 1px solid rgba(157, 78, 221, 0.3);
    border-radius: 10px;
    color: #fff;

    &:hover {
      border-color: rgba(157, 78, 221, 0.6);
    }

    &:focus {
      border-color: #9d4edd;
      box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
    }
  }

  :deep(.el-select .el-input__wrapper) {
    background: rgba(26, 21, 53, 0.8);
  }
}

.input-append-btn {
  background: rgba(157, 78, 221, 0.2);
  border: none;
  color: #c084fc;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;

  &:hover {
    background: rgba(157, 78, 221, 0.3);
  }
}

.radio-group {
  display: flex;
  gap: 20px;

  .radio-item {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    color: #a0a0c0;

    input {
      cursor: pointer;
      accent-color: #9d4edd;
    }

    &.active span {
      color: #c084fc;
    }
  }
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  color: #a0a0c0;
  font-size: 13px;

  input {
    cursor: pointer;
    accent-color: #9d4edd;
  }
}

.link-btn {
  background: none;
  border: none;
  color: #c084fc;
  cursor: pointer;
  padding: 0;
  font-size: 13px;

  &:hover {
    text-decoration: underline;
  }
}

.form-hint {
  font-size: 12px;
  color: #7a7a9a;
  margin-top: 4px;
}

.mt6 {
  margin-top: 6px;
}

.price-tip {
  margin-top: 8px;
  padding: 8px 12px;
  background: rgba(157, 78, 221, 0.1);
  border-radius: 8px;
  font-size: 12px;
  color: #a0a0c0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.countdown-text {
  margin-left: 8px;
  font-size: 12px;
  color: #fbbf24;
}

.upload-area {
  width: 100%;
}

.drop-card {
  border: 1px dashed rgba(157, 78, 221, 0.4);
  border-radius: 12px;
  background: rgba(26, 21, 53, 0.6);
  transition: all 0.2s;

  &:hover {
    border-color: #9d4edd;
    background: rgba(157, 78, 221, 0.1);
  }

  :deep(.el-upload-dragger) {
    background: transparent;
    border: none;
  }
}

.drop-inner {
  padding: 24px 16px;
  text-align: center;
}

.drop-icon {
  color: #9d4edd;
  margin-bottom: 8px;
}

.drop-title {
  font-weight: 600;
  color: #fff;
  margin-bottom: 4px;
}

.drop-sub {
  font-size: 13px;
  color: #a0a0c0;

  .link {
    color: #c084fc;
    cursor: pointer;
  }
}

.drop-meta {
  font-size: 11px;
  color: #7a7a9a;
  margin-top: 8px;
}

.queue {
  margin-top: 12px;
  max-height: 200px;
  overflow-y: auto;

  .queue-title {
    font-weight: 600;
    color: #fff;
    margin-bottom: 8px;
    font-size: 13px;
  }

  .queue-item {
    margin-bottom: 10px;

    .q-name {
      display: block;
      font-size: 11px;
      color: #a0a0c0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      margin-bottom: 4px;
    }
  }
}

.url-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.url-chip {
  background: rgba(157, 78, 221, 0.15);
  border-radius: 16px;
  padding: 4px 8px 4px 12px;
  font-size: 11px;
  color: #a0a0c0;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  .chip-duration {
    color: #4ade80;
  }

  .chip-close {
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px;
    border-radius: 50%;
    color: #a0a0c0;

    &:hover {
      color: #f87171;
    }
  }
}

.mono-input :deep(textarea) {
  font-family: monospace;
}

.opt-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.opt-name {
  font-weight: 600;
}

.opt-sub {
  color: #7a7a9a;
  font-size: 11px;
}

.opt-url {
  max-width: 400px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.preview-container {
  display: flex;
  flex-direction: column;
  gap: 12px;

  .preview-video {
    width: 100%;
    max-height: 60vh;
    border-radius: 8px;
    background: #000;
  }

  .preview-info {
    font-size: 13px;
    color: #a0a0c0;

    .info-label {
      color: #7a7a9a;
    }
  }
}

.agreement-content {
  max-height: 60vh;
  overflow: auto;

  .agreement-meta {
    color: #7a7a9a;
    font-size: 12px;
    margin-bottom: 12px;
  }

  .agreement-text {
    white-space: pre-wrap;
    word-break: break-word;
    line-height: 1.7;
    font-size: 13px;
    color: #a0a0c0;
  }
}

.dialog-btn {
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  margin-left: 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &.cancel-btn {
    background: rgba(157, 78, 221, 0.15);
    border: 1px solid rgba(157, 78, 221, 0.4);
    color: #a0a0c0;

    &:hover {
      background: rgba(157, 78, 221, 0.25);
      color: #c084fc;
    }
  }

  &.confirm-btn {
    background: linear-gradient(135deg, #6546b4, #9d4edd);
    color: #fff;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 15px rgba(157, 78, 221, 0.4);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
  }
}

:deep(.el-progress-bar__outer) {
  background: rgba(157, 78, 221, 0.2);
}

:deep(.el-progress-bar__inner) {
  background: linear-gradient(90deg, #6546b4, #9d4edd);
}

:deep(.el-checkbox__inner) {
  background: rgba(26, 21, 53, 0.8);
  border-color: rgba(157, 78, 221, 0.4);

  &:hover {
    border-color: #9d4edd;
  }
}

:deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
  background: #9d4edd;
  border-color: #9d4edd;
}

:deep(.el-empty__description p) {
  color: #a0a0c0;
}

@media (max-width: 576px) {
  .btn-text {
    display: none;
  }

  .action-btn {
    padding: 5px 8px;
  }

  .audio-player {
    max-width: 120px;
  }
}
</style>